/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { DndProvider, TouchTransition, MouseTransition } from 'react-dnd-multi-backend';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { Howler } from 'howler';

// need this polyfill for modal dialog accessibility
import 'wicg-inert';

import UsageService from 'lib/UsageWrapper';
import { initAppSource } from 'lib/AppSource';
import { setInitialRumGlobalContext, logRumError } from 'lib/DDRum';
import Env from 'lib/Env';
import QAShortcuts from 'lib/QAShortcuts';
import AuthService from 'root/lib/authService';
import createConnectionEvents from 'lib/createConnectionEvents';
import { showLoadingPercentage, redirectToTeacherDashboard } from 'lib/utilityFunctions';
import { UNLICENSED_MESSAGE } from 'lib/messages';

import store from './app/activities/store';
import Activity from './app/activities/components/Activity';
import GAME_IDS from './app/activities/hub/data/GameIds';
import Unsupported from './app/activities/components/Error/Unsupported';

// Load fonts for the app
import './app/activities/styles/fonts.css';

/**
 * If we are in a lower environment (like sandbox) then allow for QA shortcuts
 */

const setHtml5PoolSizeStorage = (n = 10) => {
  Howler.html5PoolSize = n;
  sessionStorage.setItem('html5PoolSize', n);
};

const getHtml5PoolSizeStorage = () => {
  const n = JSON.parse(sessionStorage.getItem('html5PoolSize'));
  if (!Number.isInteger(n)) {
    return 10;
  }
  return n;
};

const devBoolsKeys = [
  'isNewIOS',
  'iosPatchOff',
  'bonusHtml5Off',
];

const devBools = {};

let devlog = () => {};

if (Env.isDevEnv) {
  devlog = console.log;
  const setDevBool = {};
  for (let i = devBoolsKeys.length; i--;) {
    const key = devBoolsKeys[i];
    devBools[key] = sessionStorage.getItem(key) === 'true';
    setDevBool[key] = (bool = false) => {
      sessionStorage.setItem(key, bool);
    };
  }

  AQUA_GLOBALS.QA = {
    ...AQUA_GLOBALS.QA,
    ...QAShortcuts,
    setHtml5PoolSizeStorage,
    getHtml5PoolSizeStorage,
    setDevBool,
    sessionTimeoutIntervalMs: null,
  };
}

if ((Env.deviceDetails.isSafari || Env.deviceDetails.isIOS) && !devBools.iosPatchOff) {
  devlog('using Html5 Audio');
  Howler.html5PoolSize = getHtml5PoolSizeStorage();

  Howler._releaseHtml5Audio = (audio) => {
    // only release to the pool if it's not full
    if (audio._unlocked && Howler._html5AudioPool.length < Howler.html5PoolSize) {
      Howler._html5AudioPool.push(audio);
    }
    return Howler;
  };

  const makeUnlockedAudio = () => {
    const audio = new window.Audio();
    audio._unlocked = true;
    audio._playedOnce = false;
    audio.onended = () => {
      if (!audio._playedOnce) {
        audio._playedOnce = true;
      }
    };
    return audio;
  };

  Howler._obtainHtml5Audio = () => {
    // If the pool is, look for a howl with an unlocked node that can be unloaded
    // Unload will release any unlocked audio nodes back to the pool
    if (!Howler._html5AudioPool.length) {
      const howls = Howler._howls;
      for (let i = 0, l = howls.length; i < l; i++) {
        const howl = howls[i];
        if (
          !howl.playing()
          && howl._sounds.every(s => s && s._node)
          && howl._sounds.some(s => s._node._unlocked && s._node._playedOnce) // at least one audio node is unlocked
        ) {
          howl.unload();
          break;
        }
      }
    }
    let audio = Howler._html5AudioPool.pop();
    // Get the next object from the pool if one exists.

    if (!audio) {
      // Optimistically make unlocked audio
      audio = makeUnlockedAudio();

      // run a test audio and if it fails, be sure to set _unlocked=false on audio we returned
      const testPlay = new window.Audio().play();
      testPlay.catch(() => {
        console.warn('HTML5 Audio pool exhausted, returned a potentially locked audio object.');
        audio._unlocked = false;
      });
    }

    return audio;
  };

  const unlock = () => {
    Howler.usingWebAudio = false;
    Howler._html5AudioPool = [];
    for (let i = 0; i < Howler.html5PoolSize; i++) {
      Howler._html5AudioPool.push(makeUnlockedAudio());
    }
    document.removeEventListener('touchstart', unlock, true);
    document.removeEventListener('touchend', unlock, true);
    document.removeEventListener('click', unlock, true);
    document.removeEventListener('keydown', unlock, true);
  };

  document.addEventListener('touchstart', unlock, true);
  document.addEventListener('touchend', unlock, true);
  document.addEventListener('click', unlock, true);
  document.addEventListener('keydown', unlock, true);
} else {
  devlog('NOT using Html5 Audio');
}

const startPageVisibilityEvents = () => {
  let hidden;
  let visibilityChange;
  if (typeof document.hidden !== 'undefined') { // Opera 12.10 and Firefox 18 and later support
    hidden = 'hidden';
    visibilityChange = 'visibilitychange';
  } else if (typeof document.msHidden !== 'undefined') {
    hidden = 'msHidden';
    visibilityChange = 'msvisibilitychange';
  } else if (typeof document.webkitHidden !== 'undefined') {
    hidden = 'webkitHidden';
    visibilityChange = 'webkitvisibilitychange';
  } else {
    return;
  }

  let pausedHowls = [];

  const pause = () => {
    pausedHowls = [];
    Howler._howls.forEach((howl) => {
      // Opt-out for howls that manage visibility locally (e.g. Boost Fluency ModelExamplePlayer).
      if (howl._skipGlobalVisibilityHandling) {
        return;
      }
      const isPlaying = howl.playing();
      // iOS can suspend audio before visibilitychange fires - check _sounds directly to also catch these pre-paused howls.
      const isPrePaused = !isPlaying && (howl._sounds || []).some(s => s && s._paused && !s._ended);
      if (isPlaying) {
        howl.pause();
        pausedHowls.push(howl);
      } else if (isPrePaused) {
        pausedHowls.push(howl);
      }
    });
  };

  // If the user swipes away mid-touch, iOS intercepts the gesture and touchend never fires.
  // react-dnd's TouchBackend and RN's ResponderSystem stay in active-touch state and call
  // preventDefault() on subsequent touches, making the app unresponsive.
  // Dispatch touchend (resets TouchBackend) and touchcancel (resets RN ResponderSystem).
  // A real Touch object is required - RN's createResponderEvent.js reads changedTouches[0].force unconditionally.
  const resetTouchState = () => {
    try {
      const fakeTouch = new Touch({
        identifier: 0,
        target: document.body,
        clientX: 0,
        clientY: 0,
        force: 0,
      });
      const touchEventInit = {
        bubbles: true,
        cancelable: true,
        changedTouches: [fakeTouch],
        touches: [],
        targetTouches: [],
      };
      document.dispatchEvent(new TouchEvent('touchend', touchEventInit));
      document.dispatchEvent(new TouchEvent('touchcancel', touchEventInit));
    } catch (e) {
      document.dispatchEvent(new Event('touchend', { bubbles: true }));
      document.dispatchEvent(new Event('touchcancel', { bubbles: true }));
    }
  };

  /* Howls built before unlock() ran (e.g. a game opened without a tap first) stay on WebAudio,
  but Howler's _autoResume skips the context once usingWebAudio is false, so after a suspend
  nothing resumes it. touchend, not touchstart: the gesture resume() was verified on. */
  const contextGestures = ['touchend', 'click', 'keydown'];
  const resumeContext = (event) => {
    const stopResumingContext = () => {
      contextGestures.forEach(type => document.removeEventListener(type, resumeContext, true));
    };
    // resetTouchState() dispatches a synthetic touchend, which is not a user gesture
    if (!event.isTrusted) {
      return;
    }
    // a closed context can never resume, and Howler closes it on unload()
    if (!Howler.ctx || Howler.ctx.state === 'running' || Howler.ctx.state === 'closed') {
      stopResumingContext();
      return;
    }
    // Keep listening until the context actually runs; iOS can reject or ignore a single resume()
    Howler.ctx.resume()
      .then(() => {
        if (Howler.ctx.state === 'running') {
          stopResumingContext();
        }
      })
      .catch(() => console.warn('iOS audio context resume failed'));
  };

  const unpause = () => {
    // Snapshot and clear in one step - retryStuckAudio fires async (on next touchstart),
    // so howlsToResume must be independent of pausedHowls in case pause() fires again before then.
    const howlsToResume = pausedHowls.splice(0);
    howlsToResume.forEach(howl => howl.play());

    // iOS can silently accept play() from a visibilitychange handler (promise resolves, no playerror)
    // while leaving audio stuck - either genuinely paused or "playing" per DOM with no output.
    // On the next user gesture, retry any sound that hasn't naturally ended.
    // node.play() on an already-playing element is a safe no-op, covering both stuck cases.
    // touchstart is a real user gesture, so iOS autoplay policy allows play().
    if (Env.deviceDetails.isIOS) {
      const retryStuckAudio = () => {
        howlsToResume.forEach((howl) => {
          (howl._sounds || []).forEach((s) => {
            if (s?._node instanceof HTMLAudioElement && !s._ended) {
              s._node.play().catch(() => console.warn('iOS audio retry failed'));
            }
          });
        });
      };
      document.addEventListener('touchstart', retryStuckAudio, { capture: true, once: true });
      // Same function reference each time, so repeated lock cycles don't stack listeners
      contextGestures.forEach(type => document.addEventListener(type, resumeContext, true));
      resetTouchState();
    }
  };

  const handleVisibilityChange = () => (document[hidden] ? pause() : unpause());

  document.addEventListener(visibilityChange, handleVisibilityChange, false);
};

startPageVisibilityEvents();

// Resize the app for mobile phones (iOS) so the progress bar is not cut off
if (Env.deviceDetails.os.name === 'iOS' && window.screen.height < 1024) {
  const urlBarHeight = 89;
  const viewport = document.querySelector('meta[name=viewport]');
  viewport.setAttribute('content', `height=${576 + urlBarHeight}, user-scalable=no, maximum-scale=1.0, minimum-scale=1.0`);
  const appEl = document.getElementById('appWrapper');
  appEl.setAttribute('style', 'max-height: 576px;');
}

const hasNativeTouch = document && (document.elementsFromPoint || document.msElementsFromPoint);

const getDropTargetElementsAtPoint = (x, y, dropTargets) => dropTargets.filter((t) => {
  const { left, right, bottom, top } = t.getBoundingClientRect();
  return x >= left && x <= right && y <= bottom && y >= top;
});

const HTML5toTouch = {
  backends: [
    {
      id: 'html5',
      backend: HTML5Backend,
      transition: MouseTransition,
    },
    {
      id: 'touch',
      backend: TouchBackend,
      options: {
        getDropTargetElementsAtPoint: !hasNativeTouch && getDropTargetElementsAtPoint,
      },
      preview: true,
      transition: TouchTransition,
    },
  ],
};

const App = ({ apiVersionKeycloak, isStaffLogin, keycloakSessionId, token, deepLinkConfig, hasPrototypeParam }) => (
  <Provider store={store}>
    <DndProvider options={HTML5toTouch}>
      <Activity
        apiVersionKeycloak={apiVersionKeycloak}
        isStaffLogin={isStaffLogin}
        keycloakSessionId={keycloakSessionId}
        token={token}
        deepLinkConfig={deepLinkConfig}
        hasPrototypeParam={hasPrototypeParam}
      />
    </DndProvider>
  </Provider>
);

const authorize = async () => {
  initAppSource();
  await AuthService.init();

  const token = AuthService.getAccessToken();
  const keycloakSessionId = AuthService.getSessionId();
  const ampId = AuthService.getUserBk();
  const roles = AuthService.getRoles();

  setInitialRumGlobalContext(keycloakSessionId, ampId);

  if (!roles.length) {
    logRumError('No roles are defined for this user.');
    window.location.href = `${window.location.origin}/error.html?message=${UNLICENSED_MESSAGE}`;
    return;
  }

  const urlData = new URLSearchParams(window.location.search);
  const deepLinkData = urlData.get('link');
  const deepLinkConfig = deepLinkData ? JSON.parse(atob(deepLinkData)) : {};
  const hasPrototypeParam = urlData.has('showPrototypes');

  // teacher can access student app in demo mode if #staff-app-login or with deep link data is in url
  const hasTeacherRole = roles.includes('TEACHER') && !roles.includes('STUDENT');
  const isStaffLoginToApp = hasTeacherRole && (window.location.hash.startsWith('#staff-app-login') || !!deepLinkData);
  // navigate to the teacher dashboard if teacher logs in and did not come from TD (demo mode) or deep link
  if (hasTeacherRole && !isStaffLoginToApp) {
    redirectToTeacherDashboard();
    return;
  }

  showLoadingPercentage();

  UsageService.init({ ampId });

  const container = document.getElementById('react-root');
  const root = createRoot(container);

  root.render(
    <Unsupported>
      <App
        apiVersionKeycloak={Env.API_VERSION_KEYCLOAK}
        isStaffLogin={isStaffLoginToApp}
        keycloakSessionId={keycloakSessionId}
        token={token}
        deepLinkConfig={deepLinkConfig}
        hasPrototypeParam={hasPrototypeParam}
      />
    </Unsupported>,
  );
};

authorize();

// Online/offline events
createConnectionEvents(store, GAME_IDS.METAGAME);

App.propTypes = {
  apiVersionKeycloak: PropTypes.string.isRequired,
  isStaffLogin: PropTypes.bool.isRequired,
  keycloakSessionId: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  deepLinkConfig: PropTypes.object.isRequired,
  hasPrototypeParam: PropTypes.bool.isRequired,
};

// For testing
export default App;
